import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';

import { CircularProgress } from '@material-ui/core';
import { FormServiceContext } from 'app/utils/contexts';
import { useSelector } from 'react-redux';
import { SERVICES_CODES, SERVICES_PROPERTIES } from 'app/config/services';
import { setupCompanyInitialValues, setupCompanyPayload } from 'app/utils/forms/services/company';
import Person from 'app/components/ServicesForms/Registration/Person';
import { registrationPersonValidationSchema } from 'app/utils/forms/validationsSchemas';
import { injectIntl } from 'react-intl';
import notification from '../../../../../../config/notification';

const Company = props => {
  const {
    intl,
    classes,
    loading,
    loadingDocuments,
    setLoadingDocuments,
    onCancel,
    onSave,
    onSubmit,
    initialValues,
    setInitialValues,
  } = props;

  const [canEdit, setCanEdit] = useState(true);
  const [errorsList, setErrorsList] = useState({});

  const internalNewRequest = useSelector(state => state.forms.internalNewRequest);
  const request = useSelector(state => state.requests.details);
  const authUser = useSelector(state => state.auth.user);

  useEffect(() => {
    const init = () => {
      let temp = {};
      if (Object.keys(internalNewRequest).length > 0) {
        temp = setupCompanyInitialValues(internalNewRequest);
      }
      setInitialValues(temp);
    };
    init();
  }, [internalNewRequest, setInitialValues]);

  const [registrationType, setRegistrationType] = useState(() => {
    let user = _.cloneDeep(internalNewRequest?.user);
    if (user && user.deCpfCnpj.length <= 11) {
      return 'person';
    }
    return 'company';
  });

  const validationSchema = useMemo(() => {
    if (registrationType === 'person') {
      return registrationPersonValidationSchema;
    }
    return SERVICES_PROPERTIES[internalNewRequest.cdServico]
      ? SERVICES_PROPERTIES[internalNewRequest.cdServico].validationSchema
      : {};
  }, [registrationType, internalNewRequest.cdServico]);

  useEffect(() => {
    let tempCanEdit = true;
    if (internalNewRequest.cdServico === SERVICES_CODES.INATIVACAO_CADASTRAL) {
      tempCanEdit = false;
    }
    setCanEdit(tempCanEdit);
  }, [internalNewRequest.cdServico]);

  const setupPayload = useCallback(valuesCopy => {
    return setupCompanyPayload({ values: valuesCopy, intl, cdServico: internalNewRequest.cdServico });
  }, [internalNewRequest.cdServico, intl]);

  const handleSave = useCallback(
    (values, fromSubmit, oldData) => {
      onSave(values, fromSubmit, oldData, setupPayload);
    },
    [onSave, setupPayload],
  );

  const handleSubmit = useCallback(
    (values) => {
      onSubmit(values, onSave, setupPayload);
    },
    [onSave, onSubmit, setupPayload],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validateForm = functionValidation => {
    functionValidation.then(res => {
      if (Object.keys(res).length > 0) {
        notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
          toastId: 'VALIDATION',
          type: 'error',
        });
      }
    });
  };

  const contextProvider = useMemo(() => {
    return {
      isInternalForm: false,
      hideActionBtn: true,
      hideFinalizeBtn: false,
      mainColor: 'secondary',
      loadingDocuments,
      setLoadingDocuments,
      onCancel,
      onSave: handleSave,
      onSubmit: handleSubmit,
      initialValues,
      validationSchema,
      validateOnSave: true,
      canEdit,
      user: authUser,
      registrationType,
      setRegistrationType,
      errorsList,
      setErrorsList,
      validateForm: validateForm,
    };
  }, [
    authUser,
    canEdit,
    errorsList,
    handleSave,
    handleSubmit,
    initialValues,
    loadingDocuments,
    onCancel,
    registrationType,
    setLoadingDocuments,
    validationSchema,
    validateForm,
  ]);

  const renderRequest = () => {
    const internalCdServico = internalNewRequest.cdServico || request.cdServico;
    let Component = SERVICES_PROPERTIES[internalCdServico] ? (
      SERVICES_PROPERTIES[internalCdServico].component
    ) : (
      <></>
    );

    if (registrationType === 'person') {
      Component = Person;
    }

    return (
      <>
        {loading && (
          <div className={classes.centerProgress}>
            <CircularProgress size={20} />
          </div>
        )}
        {!loading && (
          <FormServiceContext.Provider value={contextProvider}>
            <Component {...props} />
          </FormServiceContext.Provider>
        )}
      </>
    );
  };

  return renderRequest();
};

export default injectIntl(Company);
